import { Box, Button, Skeleton, Typography } from "@mui/material";
import strings from "global/constants/StringConstants";
import styles from "../ContractSearch.styles";
import t from "../ContractSearch.tokens";
import { fill } from "../ContractSearchHelper";
import { ContractsGlyph, PlusGlyph } from "./ContractSearchIcons";

interface ContractSearchHeaderProps {
  totalIndexed: number;
  /** False when the documents list failed to load. */
  countKnown?: boolean;
  documentsLoading: boolean;
  onNewSearch: () => void;
  /** On the phone the app bar carries the screen name; the page keeps only the subtitle line. */
  isPhone?: boolean;
}

/**
 * The page header, laid out like Contacts: title and a subtitle with the bold
 * contract count on the left, the outlined "New search" action on the right.
 */
const ContractSearchHeader = ({
  totalIndexed,
  countKnown = true,
  documentsLoading,
  onNewSearch,
  isPhone = false,
}: ContractSearchHeaderProps) => {
  const copy = strings.contractSearch;

  const subtitle =
    totalIndexed > 0 ? (
      <>
        {copy.subtitleLead}{" "}
        <Box component="b" sx={styles.headerCount}>
          {totalIndexed.toLocaleString()}{" "}
          {totalIndexed === 1 ? copy.contractSingular : copy.contractPlural}
        </Box>
      </>
    ) : countKnown ? (
      copy.subtitleNone
    ) : (
      copy.subtitleUnknown
    );

  const subtitleBlock = documentsLoading ? (
    <Skeleton animation="wave" variant="text" height={22} width={260} />
  ) : (
    <Typography
      component="p"
      sx={isPhone ? styles.phoneSubtitle : styles.headerSubtitle}
    >
      {subtitle}
    </Typography>
  );

  return (
    <Box component="header" sx={styles.header}>
      <Box sx={{ minWidth: 0 }}>
        {!isPhone && (
          <Typography component="h1" sx={styles.headerTitle}>
            {copy.title}
          </Typography>
        )}
        {subtitleBlock}
      </Box>
      <Box sx={styles.headerActions}>
        <Button
          id="contract_search_new_btn"
          variant="outlined"
          onClick={onNewSearch}
          sx={styles.secondaryButton}
        >
          <PlusGlyph
            sx={{
              width: t.secondaryButton.iconSize,
              height: t.secondaryButton.iconSize,
            }}
          />
          {copy.newSearch}
        </Button>
        {documentsLoading ? (
          <Skeleton
            animation="wave"
            variant="rounded"
            width={170}
            height={t.indexedChip.height}
            sx={{ borderRadius: t.indexedChip.radius }}
          />
        ) : (
          totalIndexed > 0 && (
            <Box component="span" sx={styles.indexedChip}>
              <ContractsGlyph
                sx={{ width: t.indexedChip.iconSize, height: t.indexedChip.iconSize }}
              />
              {totalIndexed === 1
                ? copy.indexedChipOne
                : fill(copy.indexedChip, { count: totalIndexed })}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default ContractSearchHeader;
